import React, { useImperativeHandle, useState, useEffect, useMemo, useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { validateSchema } from 'utils/helpers';
import { CommonTable } from './Table';
import { texts } from 'utils/languages/fr';
import isEqual from 'lodash/isEqual';

const useStyles = makeStyles(theme => ({
    tableRow: { height: '100%' },
    label: {
        marginBottom: 16,
        '&:nth-child(3)': {
            marginTop: 40,
        },
    },
    filesTableCell: {
        border: '1px solid #ccc',
        padding: '0 10px',
        margin: 0,
        height: 1,
        borderBottom: '2px solid #0000006b',
        '&:hover': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
    },
    commitmentTableCell: {
        border: '1px solid #ccc',
        textAlign: 'center',
        padding: 0,
        margin: 0,
        height: 1,
    },
    flexContainer: {
        padding: '0px 15px',
        minHeight: 65,
        height: '100%',
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'column',
        borderBottom: '1px solid #0000006b',
        justifyContent: 'center',
        '&:hover': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
    },
    tableIcons: {
        width: 35,
    },
    disabled: {
        backgroundColor: ' #F8F8F8',
    },
    hideTable: {
        display: 'none',
    },
}));

const stateFilter = (state, id) => state.filter((_, index) => index !== +id);

export const CommonForm = React.forwardRef(
    (
        {
            TableTitle,
            TableRows,
            tableHeaders,
            tableDefaultValues,
            commonDefaultValues,
            usesPoliticallyExposedData,
            tableValuesProps,
            validationSchema,
            defaultFormErrors,
            isResubmit,
            dateKey = 'birthDate',
            formSubmitted,
            setFormSubmitted,
            displayTable = true,
            onChange,
        },
        ref
    ) => {
        const classes = useStyles();

        const tableValues = useMemo(
            () =>
                isResubmit && tableValuesProps?.length ? tableValuesProps : [tableDefaultValues],
            [isResubmit, tableDefaultValues, tableValuesProps]
        );

        const [tableState, setTableState] = useState(tableValues);
        const [tableFormErrors, setTableFormErrors] = useState([defaultFormErrors]);

        const addRow = useCallback(() => {
            setTableFormErrors(prevState => [...prevState, defaultFormErrors]);
            setTableState(prevState => [...prevState, tableDefaultValues]);
        }, [defaultFormErrors, tableDefaultValues]);

        // remove complete row from state and files from state and DB
        const removeRow = useCallback(
            ({ currentTarget: { id } }) => {
                const isLastRow = tableState?.length === 1; // last row

                setTableState(prevState =>
                    prevState.filter((form, formIndex) => {
                        return formIndex !== +id;
                    })
                );
                setTableFormErrors(prevState => stateFilter(prevState, id));

                if (isLastRow) {
                    addRow();
                }
            },
            [addRow, tableState?.length]
        );

        //add files to row
        const handleFileChange =
            (formSetter, rowIndex) =>
            ({ target: { files } }) => {
                const file = files[0];
                if (file) {
                    formSetter(prevState =>
                        prevState.map((form, index) => {
                            if (index === rowIndex) {
                                return {
                                    ...form,
                                    documents: [...form.documents, file],
                                };
                            } else {
                                return form;
                            }
                        })
                    );
                }
            };

        // remove single file from row
        const removeFileFromState = (rowIndex, fileId) => {
            setTableState(prevState =>
                prevState.map((form, index) => {
                    if (index === rowIndex) {
                        return {
                            ...form,
                            documents: stateFilter(form.documents, fileId),
                        };
                    } else {
                        return form;
                    }
                })
            );
        };

        // remove single file from state or DB
        const removeFile = ({ currentTarget: { id } }, rowIndex) => {
            tableState.forEach((form, formIndex) => {
                if (formIndex === rowIndex) {
                    removeFileFromState(rowIndex, id);
                }
                return form;
            });
        };

        const handleChange = ({ target: { value, name } }, id, formSetter) => {
            formSetter(prevState => {
                const PER = !usesPoliticallyExposedData
                    ? undefined
                    : {
                          politicallyExposedReason:
                              name === 'politicallyExposedPerson'
                                  ? { 1: false, 2: false, 3: false }
                                  : prevState[id].politicallyExposedReason,
                      };
                return [
                    ...prevState.slice(0, id),
                    {
                        ...prevState[id],
                        [name]: name === 'nif' ? value.replace(/\s/gi, '') : value,
                        ...PER,
                    },
                    ...prevState.slice(id + 1),
                ];
            });
        };

        const handleAddressChange =
            (id, formSetter) =>
            ({ formattedAddress }) => {
                formSetter(prevState => {
                    return [
                        ...prevState.slice(0, id),
                        { ...prevState[id], address: formattedAddress },
                        ...prevState.slice(id + 1),
                    ];
                });
            };

        const handleRegistrationAddressChange =
            (id, formSetter) =>
            ({ formattedAddress }) => {
                formSetter(prevState => {
                    return [
                        ...prevState.slice(0, id),
                        { ...prevState[id], registrationAddress: formattedAddress },
                        ...prevState.slice(id + 1),
                    ];
                });
            };

        const handleDateChange = (value, id) =>
            setTableState(prevState =>
                prevState.map((form, rowIndex) => {
                    if (rowIndex === id) {
                        return { ...form, [dateKey]: value ? value.format('YYYY-MM-DD') : null };
                    } else {
                        return form;
                    }
                })
            );

        const handleRadioButtons = ({ target: { value, name } }, id, formSetter) => {
            formSetter(prevState =>
                prevState.map((form, rowIndex) => {
                    if (rowIndex === id) {
                        return {
                            ...form,
                            [name]: value,
                            ...commonDefaultValues,
                        };
                    } else {
                        return form;
                    }
                })
            );
        };

        const handlePoliticallyExposedReasonChange = useCallback(
            (id, formSetter) => (e, index) => {
                const { checked } = e.target;
                formSetter(prevState => {
                    return [
                        ...prevState.slice(0, id),
                        {
                            ...prevState[id],
                            politicallyExposedReason: {
                                ...prevState[id].politicallyExposedReason,
                                [index]: checked,
                            },
                        },
                        ...prevState.slice(id + 1),
                    ];
                });
            },
            []
        );

        // update default values for form errors
        const updateRows = useCallback(
            (errorsRowSetter, index) => {
                errorsRowSetter(prevState => [
                    ...prevState.slice(0, index),
                    defaultFormErrors,
                    ...prevState.slice(index + 1),
                ]);
            },
            [defaultFormErrors]
        );

        // update default values for form errors
        useEffect(() => {
            if (isResubmit && formSubmitted) {
                setTableState(tableValues);
                setFormSubmitted(false);
            }

            tableValues?.forEach((_, i) => {
                updateRows(setTableFormErrors, i);
            });
        }, [tableValues, isResubmit, updateRows, formSubmitted, setFormSubmitted]);

        const validateForm = () => {
            let tableFormValid = [];
            tableState?.forEach(form => {
                const { valid, newErrors } = validateSchema(validationSchema, form);
                if (form.politicallyExposedPerson === 'true') {
                    const reasons = form.politicallyExposedReason
                        ? Object.keys(form.politicallyExposedReason)?.filter(
                              id => form.politicallyExposedReason[id] === true
                          )
                        : [];
                    if (reasons.length === 0) {
                        tableFormValid.push({
                            valid: false,
                            newErrors: {
                                ...newErrors,
                                politicallyExposedReason: texts.MOINS_UNE_CASE,
                            },
                        });
                        return;
                    }
                }
                const isEmptyLine = isEqual(form, tableDefaultValues);
                tableFormValid.push({ valid, newErrors, isEmptyLine });
            });

            return tableFormValid;
        };

        // pass the validation action and state to parent component
        useImperativeHandle(ref, () => ({
            validateAllForms() {
                return validateForm();
            },
            getFormState() {
                return tableState;
            },
            setFormErrors(tableErrors) {
                setTableFormErrors(tableErrors.map(error => error.newErrors));
            },
            clearTableState() {
                setTableState([tableDefaultValues]);
            },
            isEmpty() {
                return tableState.every(row => isEqual(row, tableDefaultValues));
            },
        }));

        useEffect(() => {
            onChange && onChange();
        }, [tableState, onChange]);

        return (
            <div ref={ref} className={!displayTable ? classes.hideTable : null}>
                <TableTitle />
                <CommonTable tableHeaders={tableHeaders} addRow={addRow}>
                    {tableState.map((form, index) => (
                        <TableRows
                            key={tableHeaders + index}
                            values={form}
                            handleChange={e => handleChange(e, index, setTableState)}
                            handleAddressChange={handleAddressChange(index, setTableState)}
                            handleRegistrationAddressChange={handleRegistrationAddressChange(
                                index,
                                setTableState
                            )}
                            handleDateChange={e => handleDateChange(e, index)}
                            handlePoliticallyExposedReasonChange={handlePoliticallyExposedReasonChange(
                                index,
                                setTableState
                            )}
                            classes={classes}
                            index={index}
                            removeRow={removeRow}
                            errors={tableFormErrors[index]}
                            handleTableFileChange={handleFileChange(setTableState, index)}
                            removeFile={e => removeFile(e, index)}
                            handleRadioButtons={e => handleRadioButtons(e, index, setTableState)}
                        />
                    ))}
                </CommonTable>
            </div>
        );
    }
);
